import { useState, useEffect, useRef, useCallback, Fragment, isValidElement, cloneElement } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Recursively strips data-testid so the off-screen measuring clone below
// doesn't leave duplicate test ids in the DOM alongside the real, visible
// copy of each item.
function stripTestIds(node) {
  if (!isValidElement(node)) return node;
  const { "data-testid": _omit, children, ...rest } = node.props;
  const newChildren = Array.isArray(children) ? children.map(stripTestIds) : stripTestIds(children);
  return cloneElement(node, rest, newChildren);
}

/**
 * Lays nav items out in one row and tucks whichever ones don't fit into a
 * "More" dropdown, instead of letting them wrap onto a second line.
 * `items` is [{ key, element }] — each `element` keeps its own full
 * styling/behaviour (hover colours, dropdown-on-hover children, etc.),
 * this component only decides how many fit before the "More" trigger.
 */
export function OverflowNav({ items, className, gap = 12, textColor, bgColor, fontSize, linkStyle, ...rest }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const recalc = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const containerWidth = container.getBoundingClientRect().width;
    const moreBtnWidth = 90;
    const children = Array.from(measure.children);
    let total = 0;
    let count = 0;
    for (let i = 0; i < children.length; i++) {
      const w = children[i].getBoundingClientRect().width + (count > 0 ? gap : 0);
      const reserve = i < children.length - 1 ? moreBtnWidth + gap : 0;
      if (total + w <= containerWidth - reserve) {
        total += w;
        count++;
      } else {
        break;
      }
    }
    setVisibleCount(count);
  }, [gap]);

  useEffect(() => {
    recalc();
    const ro = new ResizeObserver(recalc);
    // Also watch the measuring clone — item content (e.g. "Login" becoming
    // "Sign Out") or late web-font swaps can change natural widths without
    // the container itself resizing.
    if (containerRef.current) ro.observe(containerRef.current);
    if (measureRef.current) ro.observe(measureRef.current);
    window.addEventListener("resize", recalc);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, recalc]);

  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);

  return (
    <div ref={containerRef} className={className} style={{ gap, minWidth: 0, flex: "1 1 auto" }} {...rest}>
      {/* Off-screen clone used only to measure each item's natural width */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{ position: "absolute", visibility: "hidden", pointerEvents: "none", top: -9999, left: -9999, display: "flex", gap }}
      >
        {items.map(({ key, element }) => <div key={key}>{stripTestIds(element)}</div>)}
      </div>

      {visible.map(({ key, element }) => <Fragment key={key}>{element}</Fragment>)}

      {overflow.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="font-medium tracking-wide transition-all flex items-center gap-1"
              style={{ fontSize, ...linkStyle, color: textColor, opacity: 0.75 }}
              data-testid="nav-more-btn"
            >
              More <ChevronDown size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            style={{ backgroundColor: bgColor === "transparent" ? "var(--nav-bg, #ffffff)" : bgColor }}
            data-testid="nav-more-menu"
          >
            {overflow.map(({ key, element }) => (
              <DropdownMenuItem key={key} asChild className="cursor-pointer">
                {element}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export default OverflowNav;
